import type { BillOut } from "../../../../api/bills";
import type { OrderingTableOrder } from "../../../../api/configuration";

// Подредбата на масите в списъка на „Маркиране" — ЕДНО място за двата
// потребителя: `Ordering.tsx` (авто-избор на първата маса) и `TableList.tsx`
// (картите). Закритите маси са винаги най-отдолу; настройката на ресторанта
// `orderingTableOrder` решава реда сред отворените (и сред закритите):
// - chronological — по време на отваряне (най-старата първа), без created_at — по id;
// - alphanumeric — първо масите с чисто числово име по номер (1, 2, 10, 33),
//   после именуваните по азбучен ред („Бар", „Тераса 2" преди „Тераса 10");
//   равни имена (лична номерация) се разрешават хронологично.

/** Числово ли е името на масата — само цифри, напр. „12"; „12а" не е. */
const tableNumberOf = (name: string): number | null =>
  /^\d+$/.test(name.trim()) ? Number(name.trim()) : null;

const byOpenedAt = (a: BillOut, b: BillOut): number => {
  if (a.created_at && b.created_at) {
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  }
  return a.id - b.id;
};

const byNameAlphanumeric = (a: BillOut, b: BillOut): number => {
  const aNumber = tableNumberOf(a.name);
  const bNumber = tableNumberOf(b.name);
  if (aNumber !== null && bNumber !== null) return aNumber - bNumber;
  if (aNumber !== null) return -1;
  if (bNumber !== null) return 1;
  return a.name.trim().localeCompare(b.name.trim(), "bg", {
    numeric: true,
    sensitivity: "base",
  });
};

/**
 * Филтрира (закритите празни маси при скрит списък) и подрежда масите за
 * списъка. Връща нов масив, входът не се променя. `closingBillIds` — масите,
 * чието закриване още чака принтера: те НЕ се крият, каквато и да е
 * настройката, докато кутийката им се върти.
 */
export const sortBillsForTableList = (
  bills: BillOut[],
  showClosedTables: boolean,
  order: OrderingTableOrder,
  closingBillIds?: ReadonlySet<number>,
): BillOut[] => {
  const visible = showClosedTables
    ? bills
    : bills.filter(
        (bill) =>
          closingBillIds?.has(bill.id) ||
          !(bill.status === "closed" && parseFloat(bill.total) === 0),
      );
  return [...visible].sort((a, b) => {
    const aIsClosed = a.status === "closed";
    const bIsClosed = b.status === "closed";
    if (aIsClosed !== bIsClosed) return aIsClosed ? 1 : -1;
    if (order === "alphanumeric") {
      const byName = byNameAlphanumeric(a, b);
      if (byName !== 0) return byName;
    }
    return byOpenedAt(a, b);
  });
};
